import { useMemo } from "react";
import { useDashboardAnalyticsApi } from "./useDashboardAnalytics";

/**
 * Compute the global compliance-readiness score (0–100) for the topbar
 * `ReadinessChip`. Reuses the React Query cache populated by the dashboard
 * so it costs no extra network when the dashboard is already loaded.
 *
 * Returns `undefined` while loading or when there is not enough data to
 * compute a meaningful score, so the topbar can hide the chip rather than
 * show a stale placeholder.
 *
 * Score is: (resolved + closed) / total × 100 — stable global ratio across
 * all non-deleted cases for the tenant.
 */
export function useReadinessScore(
  options: { enabled?: boolean } = {},
): number | undefined {
  const enabled = options.enabled ?? true;

  const { data: analyticsData, isLoading } = useDashboardAnalyticsApi(
    {},
    { enabled },
  );

  return useMemo(() => {
    if (!enabled) return undefined;
    if (isLoading) return undefined;
    if (!analyticsData?.statusMetrics) return undefined;

    const { total, resolved, closed } = analyticsData.statusMetrics;
    if (!total) return undefined;

    return Math.round(((resolved + closed) / total) * 100);
  }, [enabled, analyticsData, isLoading]);
}
